/**
 * Group UI utility surface.
 *
 * Feature entrypoints live at `auth/group/detail` and `auth/group/manage`.
 */
import { Space } from "@corpuscomplete/core/auth/space";

export namespace GroupUI {
  export type GrantLike = { resourceType: string; resourceScope: string; role: string };

  export type Member = NonNullable<Space.Info["group"]>["members"][number];

  export type MemberWithGrants = {
    member: Member;
    grants: GrantLike[];
    rootRole: Space.RootRole;
    membersRole: Space.ContentsRole;
  };

  export type RootRole = Space.RootRole;
  export type MembersRole = Space.ContentsRole;
  export type AllowedRoleChanges = Space.AllowedRoleChanges;
  export type SelfRoleChanges = Space.SelfRoleChanges;
  export type Invite = Space.InvitePreview;

  export function getMemberRootRole(memberGrants: GrantLike[]): RootRole {
    return (Space.getGrant(memberGrants, "root")?.role as RootRole) ?? null;
  }

  export function getMemberMembersRole(memberGrants: GrantLike[]): MembersRole {
    return (Space.getGrant(memberGrants, "contents")?.role as MembersRole) ?? null;
  }

  export function formatRoleDisplay(rootRole: RootRole, membersRole: MembersRole): string | null {
    const parts: string[] = [];

    if (rootRole) {
      parts.push(`Group: ${capitalize(rootRole)}`);
    }

    if (membersRole) {
      parts.push(`Contents: ${capitalize(membersRole)}`);
    }

    return parts.length > 0 ? parts.join(" · ") : null;
  }

  export function partitionMembersByGrants(
    members: readonly Member[],
    grantsByUserId: Map<string, GrantLike[]>,
  ): { withGrants: MemberWithGrants[]; withoutGrants: Member[] } {
    const withGrants: MemberWithGrants[] = [];
    const withoutGrants: Member[] = [];

    for (const member of members) {
      const grants = grantsByUserId.get(member.userId) ?? [];
      const rootRole = getMemberRootRole(grants);
      const membersRole = getMemberMembersRole(grants);

      if (rootRole || membersRole) {
        withGrants.push({ member, grants, rootRole, membersRole });
      } else {
        withoutGrants.push(member);
      }
    }

    return { withGrants, withoutGrants };
  }
}

function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}
